import React, { useState, useEffect } from 'react';
import {
  Spinner,
  Center,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Button
} from '@chakra-ui/react';
import NavSidebar from './components/NavSidebar';
import DashboardHome from './components/DashboardHome';
import DashboardAccount from './DashboardAccount';
import RegisteredProducts from './components/RegisteredProducts';
import MyLibrary from './MyLibrary';
import { fetchUserData, clearUserDataCache } from './services/api';

const Dashboard = ({ 
  apiDomain = 'https://api.calibrite.com',
  // Allow story props to override defaults
  storyData = null,
  onLogout
}) => {
  // User profile state - initialize with mock data if in Storybook
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('en');
  const [activeSection, setActiveSection] = useState('home');
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  // Initial load of user data
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await fetchUserData(true);
        if (userData && userData.user) {
          const user = userData.user;
          setFirstName(user.firstName || '');
          setLastName(user.lastName || '');
          setEmail(user.email || '');
          setCountry(user.countryCode || '');
          setLanguage(user.locale || 'en');
        }
      } catch (err) {
        console.error('Failed to load user data:', err);
        toast({
          title: 'Authentication Error',
          description: 'Failed to load your profile. Please check if you are logged in.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadUserData();

    return () => {
      clearUserDataCache();
    };
  }, [toast]);

  const handleNavigation = (section) => {
    setActiveSection(section);
  };

  const handleLogout = async () => {
    try {
      // Call the onLogout prop if provided
      if (onLogout) {
        await onLogout();
      }
      
      // Clear local storage
      localStorage.removeItem('payloadToken');

      // Redirect to the login page
      window.location.href = '/login';
    } catch (err) {
      console.error('Logout failed:', err);
      toast({
        title: 'Logout Failed',
        description: 'There was an error logging out. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const ProfileMenu = () => (
    <Menu>
      <MenuButton
        as={Button}
        colorScheme="ghost"
        padding={0}
        _hover={{ bg: 'transparent' }}
      >
        <Avatar
          size="sm"
          name={`${firstName} ${lastName}`}
          bg="blue.500"
          color="white"
        />
      </MenuButton>
      <MenuList bg="white" color="gray.800">
        <MenuItem onClick={() => handleNavigation('account')}>My Account</MenuItem>
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </MenuList>
    </Menu>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'home':
        return <DashboardHome firstName={firstName} />;
      case 'account':
        return (
          <DashboardAccount 
            firstName={firstName} 
            lastName={lastName} 
            email={email} 
            password={password} 
            country={country} 
            language={language}
            setFirstName={setFirstName}
            setLastName={setLastName}
            setEmail={setEmail}
            setPassword={setPassword}
            setCountry={setCountry}
            setLanguage={setLanguage}
          />
        );
      case 'products':
        return <RegisteredProducts />;
      case 'library':
        return <MyLibrary />;
      default:
        return null;
    }
  };

  // If still loading, show a loading spinner
  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <NavSidebar 
      onNavigate={handleNavigation} 
      activeItem={activeSection}
      profileMenu={<ProfileMenu />}
    >
      {renderContent()}
    </NavSidebar>
  );
};

export default Dashboard; 